import { useCallback, useEffect, useState } from 'react';
import {
  inventoryPlanningApi,
  type EchelonNode,
  type InventoryDashboard,
  type InventoryPlan,
  type InventoryPolicy,
  type InventoryRecommendation,
  type InventoryRisk,
  type InventoryScenario,
  type InventorySegment,
  type ReplenishmentPlan,
  type SafetyStockRecord,
} from '../../api/inventoryplanning';

type Tab = 'dashboard' | 'policies' | 'safety' | 'segmentation' | 'risks' | 'replenishment' | 'recommendations' | 'scenarios' | 'echelons';
type Theme = 'light' | 'dark';

function fmtNum(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function InventoryPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<InventoryDashboard | null>(null);
  const [plan, setPlan] = useState<InventoryPlan | null>(null);
  const [policies, setPolicies] = useState<InventoryPolicy[]>([]);
  const [safetyStock, setSafetyStock] = useState<SafetyStockRecord[]>([]);
  const [segments, setSegments] = useState<InventorySegment[]>([]);
  const [risks, setRisks] = useState<InventoryRisk[]>([]);
  const [replenishments, setReplenishments] = useState<ReplenishmentPlan[]>([]);
  const [recommendations, setRecommendations] = useState<InventoryRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<InventoryScenario[]>([]);
  const [echelons, setEchelons] = useState<EchelonNode[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans, pols, segs] = await Promise.all([
        inventoryPlanningApi.dashboard(),
        inventoryPlanningApi.listPlans(),
        inventoryPlanningApi.listPolicies(),
        inventoryPlanningApi.listSegments(),
      ]);
      setDashboard(dash);
      setPolicies(pols);
      setSegments(segs);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;

      const [ss, rsk, repl, recs, sc, ech] = await Promise.all([
        inventoryPlanningApi.listSafetyStock(p.id),
        inventoryPlanningApi.listRisks(p.id),
        inventoryPlanningApi.listReplenishments(p.id),
        inventoryPlanningApi.listRecommendations(p.id),
        inventoryPlanningApi.listScenarios(p.id),
        inventoryPlanningApi.listEchelons(p.id),
      ]);
      setSafetyStock(ss);
      setRisks(rsk);
      setReplenishments(repl);
      setRecommendations(recs);
      setScenarios(sc);
      setEchelons(ech);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inventory planning');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'policies', label: 'Policy Manager' },
    { id: 'safety', label: 'Safety Stock' },
    { id: 'segmentation', label: 'ABC-XYZ Matrix' },
    { id: 'risks', label: 'Risk Dashboard' },
    { id: 'replenishment', label: 'Replenishment' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'scenarios', label: 'Scenarios' },
    { id: 'echelons', label: 'Multi-Echelon' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Inventory Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>
              Safety stock optimization, replenishment intelligence, ABC-XYZ segmentation, and multi-echelon planning
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">
              {dark ? 'Light' : 'Dark'}
            </button>
            <button type="button" onClick={refresh} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700">
              Refresh
            </button>
          </div>
        </div>
        {plan && (
          <p className={`mt-2 text-sm ${muted}`}>
            {plan.name} · Target {fmtNum(plan.target_stock_units)} · Safety {fmtNum(plan.safety_stock_units)} ·{' '}
            {plan.days_of_supply.toFixed(0)} days supply · Health {plan.health_score.toFixed(0)}
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">
          {error}
        </div>
      )}

      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Inventory planning sections">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500'}`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Plans', value: dashboard.active_plans },
              { label: 'Avg Days of Supply', value: dashboard.avg_days_of_supply.toFixed(0) },
              { label: 'Service Level', value: `${(dashboard.avg_service_level * 100).toFixed(0)}%` },
              { label: 'Health Score', value: dashboard.health_score.toFixed(0) },
              { label: 'Stockout %', value: `${dashboard.stockout_pct}%` },
              { label: 'Overstock %', value: `${dashboard.overstock_pct}%` },
              { label: 'Working Capital', value: fmtNum(dashboard.working_capital) },
              { label: 'Open Risks', value: dashboard.open_risks },
            ].map((m) => (
              <div key={m.label} className={card}>
                <p className={`text-sm ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'policies' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {policies.map((p) => (
              <div key={p.id} className={card}>
                <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900 dark:text-teal-300">{p.policy_type.replace(/_/g, ' ')}</span>
                <h3 className="mt-2 font-medium">{p.name}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{p.description}</p>
                <p className="mt-2 text-sm">Min {fmtNum(p.min_stock)} · Max {fmtNum(p.max_stock)} · Reorder {fmtNum(p.reorder_point)}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'safety' && safetyStock.map((ss) => (
          <div key={ss.id} className={`${card} max-w-2xl`}>
            <h2 className="text-lg font-medium">Safety Stock Analysis</h2>
            <p className={`mt-2 text-sm ${muted}`}>{ss.explanation}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
              <p>Safety Stock: <strong>{fmtNum(ss.safety_stock)}</strong></p>
              <p>Buffer Stock: <strong>{fmtNum(ss.buffer_stock)}</strong></p>
              <p>Seasonal Buffer: <strong>{fmtNum(ss.seasonal_buffer)}</strong></p>
              <p>Lead Time Buffer: <strong>{fmtNum(ss.lead_time_buffer)}</strong></p>
              <p>Service Level: <strong>{(ss.service_level * 100).toFixed(0)}%</strong></p>
              <p>Method: <strong>{ss.calculation_method.replace(/_/g, ' ')}</strong></p>
            </div>
          </div>
        ))}

        {tab === 'segmentation' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={muted}><th className="p-2 text-left">SKU</th><th className="p-2">ABC</th><th className="p-2">XYZ</th><th className="p-2">Matrix</th><th className="p-2">Lifecycle</th><th className="p-2 text-right">Volume</th></tr></thead>
              <tbody>
                {segments.map((s) => (
                  <tr key={s.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                    <td className="p-2">{s.sku_label ?? s.sku}</td>
                    <td className="p-2 text-center font-medium">{s.abc_segment}</td>
                    <td className="p-2 text-center font-medium">{s.xyz_segment}</td>
                    <td className="p-2 text-center">{s.matrix_cell}</td>
                    <td className="p-2 text-center">{s.lifecycle_stage.replace(/_/g, ' ')}</td>
                    <td className="p-2 text-right">{fmtNum(s.annual_volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'risks' && (
          <div className="space-y-4">
            {risks.map((r) => (
              <div key={r.id} className={card}>
                <div className="flex justify-between">
                  <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900 dark:text-red-300">{r.risk_type.replace(/_/g, ' ')}</span>
                  <span className={`text-sm ${muted}`}>Confidence {(r.confidence * 100).toFixed(0)}%</span>
                </div>
                <p className="mt-2 text-sm"><strong>Impact:</strong> {r.impact}</p>
                <p className="mt-1 text-sm"><strong>Action:</strong> {r.recommended_action}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'replenishment' && (
          <div className="space-y-4">
            {replenishments.map((r) => (
              <div key={r.id} className={card}>
                <div className="flex justify-between">
                  <h3 className="font-medium">{fmtNum(r.quantity)} units from {r.preferred_source}</h3>
                  <span className={`rounded px-2 py-0.5 text-xs ${r.urgency === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.urgency}</span>
                </div>
                <p className={`mt-1 text-sm ${muted}`}>{r.business_justification}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'recommendations' && (
          <div className="space-y-4">
            {recommendations.map((rec) => (
              <div key={rec.id} className={card}>
                <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900 dark:text-teal-300">{rec.type.replace(/_/g, ' ')}</span>
                <h3 className="mt-2 font-medium">{rec.title}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{rec.business_explanation}</p>
                <p className="mt-2 text-sm">Benefit: {rec.expected_benefit} · Savings: {fmtNum(rec.estimated_savings)}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'scenarios' && (
          <div className="grid gap-4 md:grid-cols-3">
            {scenarios.map((sc) => (
              <div key={sc.id} className={card}>
                <span className="rounded bg-slate-200 px-2 py-0.5 text-xs dark:bg-slate-700">{sc.scenario_type.replace(/_/g, ' ')}</span>
                <h3 className="mt-2 font-medium">{sc.name}</h3>
                <p className="mt-2 text-xl font-semibold">{fmtNum(sc.inventory_units)} units</p>
                <p className={`text-sm ${muted}`}>WC {fmtNum(sc.working_capital)} · Risk {sc.risk_score} · SL {(sc.service_level * 100).toFixed(0)}%</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'echelons' && (
          <div className="grid gap-4 md:grid-cols-3">
            {echelons.map((e) => (
              <div key={e.id} className={card}>
                <p className={`text-xs uppercase ${muted}`}>{e.level.replace(/_/g, ' ')}</p>
                <h3 className="mt-1 font-medium">{e.location_name}</h3>
                <p className="mt-2 text-sm">Target {fmtNum(e.target_stock)} · Current {fmtNum(e.current_stock)} · Safety {fmtNum(e.safety_stock)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
